/**
 * Candidate picker for a needs-selection row with *multiple* candidates (or to
 * re-pick a blue Status cell). Single-candidate rows are reviewed inline in the
 * validator now, so this modal is the ambiguous-case path. Lists the row's
 * Tekion candidates; clicking one attributes it to the vehicle (-> user_selected
 * / blue). The suggested candidate is highlighted.
 */
import React from "react"
import { X } from "lucide-react"

import { WholesaleVehicle } from "../../api/wholesale"
import { THEME } from "../../config/theme"
import { AdesaFields, CandidateFields } from "./clearedMatchFields"

interface Props {
  vehicle: WholesaleVehicle
  onSelect: (tekionStock: string) => void
  onExclude: () => void
  onClose: () => void
  loading: boolean
}

export default function ClearedCandidateModal({ vehicle, onSelect, onExclude, onClose, loading }: Props) {
  const candidates = vehicle.match_candidates ?? []
  const ymm = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || "Vehicle"

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={titleStyle}>Pick the Tekion record</div>
            <div style={subtitleStyle}>{ymm}</div>
            <div style={{ marginTop: 6 }}>
              <AdesaFields vehicle={vehicle} />
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeStyle} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div style={listStyle}>
          {candidates.length === 0 ? (
            <div style={emptyStyle}>No candidate records.</div>
          ) : (
            candidates.map((c) => {
              const isCurrent = c.tekion_stock === vehicle.tekion_stock
              const isSuggested = c.tekion_stock === vehicle.suggested_tekion_stock
              const chips = (
                <>
                  {isCurrent && <span style={chipStyle(THEME.colors.navy)}>Current</span>}
                  {!isCurrent && isSuggested && (
                    <span style={chipStyle(THEME.colors.neutral.light.textSecondary)}>Suggested</span>
                  )}
                </>
              )
              return (
                <button
                  key={c.tekion_stock ?? Math.random()}
                  type="button"
                  disabled={loading || !c.tekion_stock}
                  onClick={() => c.tekion_stock && onSelect(c.tekion_stock)}
                  style={cardStyle(isCurrent)}
                >
                  <CandidateFields vehicle={vehicle} candidate={c} withMeta trailingStock={chips} />
                </button>
              )
            })
          )}
        </div>

        <div style={footerStyle}>
          <span style={footerHintStyle}>Not the right car?</span>
          <button type="button" onClick={onExclude} disabled={loading} style={excludeButtonStyle}>
            Exclude from cleared list
          </button>
        </div>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: THEME.spacing.md,
}

const modalStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.lg,
  width: "100%",
  maxWidth: 520,
  maxHeight: "80vh",
  display: "flex",
  flexDirection: "column",
  fontFamily: THEME.fonts.primary,
  overflow: "hidden",
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: THEME.spacing.md,
  padding: THEME.spacing.md,
  borderBottom: `1px solid ${THEME.colors.silver}`,
}

const titleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 2,
}

const closeStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  padding: 2,
  display: "flex",
}

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.md,
  overflowY: "auto",
}

function cardStyle(current: boolean): React.CSSProperties {
  return {
    textAlign: "left",
    padding: THEME.spacing.md,
    borderRadius: THEME.borderRadius.md,
    border: `1px solid ${current ? THEME.colors.navy : THEME.colors.silver}`,
    background: current ? THEME.colors.neutral.light.background : THEME.colors.neutral.light.surface,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
  }
}

function chipStyle(bg: string): React.CSSProperties {
  return {
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: THEME.colors.white,
    background: bg,
    borderRadius: THEME.borderRadius.sm,
    padding: "2px 6px",
  }
}

const emptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  fontStyle: "italic",
  padding: THEME.spacing.md,
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
  padding: THEME.spacing.md,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const footerHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
}

const excludeButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
}
